// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';
import type { PostItem } from 'store/modules/listing';
import { getScrollBottom } from 'lib/common';
import { SearchActions } from 'store/actionCreators';
import * as CommonAPI from 'lib/api/common';
import { track, TRACKING_EVENTS } from 'lib/tracking';
import type { State } from '../../../store';
import SearchResultItem from '../SearchResultItem';
import SearchHistory from '../SearchHistory';
import SearchFacets from '../SearchFacets';
import './SearchResults.scss';

type Props = {
  result: ?{
    count: number,
    data: PostItem[],
    estimatedTotalHits: number,
    hits: PostItem[],
    facetDistribution: ?Object,
  },
  onSearchNext: () => any,
  menu: string,
  currentKeyword: string,
  onFacetChange: (facet: string, value: string) => void,
  selectedFacets: Object,
  onSortChange: (sortBy: string) => void,
  currentSort: string,
  currentUser: ?Object,
  onKeywordChange: (newKeyword: string) => void,
};

class SearchResults extends Component<Props> {
  state = {
    badgesMap: {},
    refinedKeyword: '',
  };

  async componentDidMount() {
    this.listenScroll();
    await this.loadBadges();
  }

  onContactSupport = async () => {
    const { currentKeyword, currentUser } = this.props;

    if (window.Tawk_API) {
      window.Tawk_API.toggle();
      track(TRACKING_EVENTS.CHAT_WIDGET_OPENED, {
        context: 'search_no_results',
        searchKeyword: currentKeyword,
      });

      const url = 'https://hooks.slack.com/services/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';
      const data = {
        channel: '#제품상담',
        username: 'cacheby-bot',
        text: `*🔍 검색 결과 없음 - 제품 문의*`,
        blocks: [
          {
            type: 'header',
            text: {
              type: 'plain_text',
              text: '🔍 검색 결과 없음 - 제품 문의',
              emoji: true,
            },
          },
          {
            type: 'divider',
          },
          {
            type: 'section',
            fields: [
              {
                type: 'mrkdwn',
                text: `*문의자 정보:*\n👤 *이름:* ${
                  currentUser ? currentUser.displayName : '비회원'
                }\n📧 *이메일:* ${currentUser ? currentUser.email : '비회원'}`,
              },
              {
                type: 'mrkdwn',
                text: `*문의 시간:*\n🕒 ${new Date().toLocaleString('ko-KR', {
                  timeZone: 'Asia/Seoul',
                })}`,
              },
            ],
          },
          {
            type: 'divider',
          },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*검색어:*\n${currentKeyword || '(검색어 없음)'}`,
            },
          },
        ],
      };

      try {
        await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      } catch (e) {
        console.error('Slack notification failed:', e);
      }
    }
  };

  loadBadges = async () => {
    try {
      const response = await CommonAPI.getAllBadges();
      const badges = response.data;
      const badgesMap = {};
      badges.forEach(badge => {
        badgesMap[badge.name] = badge.color;
      });
      this.setState({ badgesMap });
    } catch (e) {
      console.error('Failed to load badges:', e);
    }
  };

  onScroll = throttle(() => {
    const scrollBottom = getScrollBottom();
    if (scrollBottom > 1000) return;
    const { result, onSearchNext } = this.props;
    if (result && result.estimatedTotalHits > result.hits.length) onSearchNext();
  }, 250);

  listenScroll = () => {
    window.addEventListener('scroll', this.onScroll);
  };

  unlistenScroll = () => {
    window.removeEventListener('scroll', this.onScroll);
  };

  getFacetLabel = (key: string): string => {
    const labels = {
      categories: '카테고리',
      brand: '브랜드',
      tags: '태그',
      badges: '배지',
      units: '단위',
      price: '가격대',
    };
    return labels[key] || key;
  };

  handleRemoveFacet = (facetKey: string, facetValue: string) => {
    const { selectedFacets, onFacetChange } = this.props;

    if (facetKey === 'price') {
      // 가격대는 단일 선택이므로 전체 해제
      onFacetChange('price', '');
    } else {
      // 다른 facet은 해당 값만 제거
      const currentValues = selectedFacets[facetKey].split(',').map(v => v.trim());
      const newValues = currentValues.filter(v => v !== facetValue);
      onFacetChange(facetKey, newValues.length > 0 ? newValues.join(',') : '');
    }
  };

  renderSelectedFacets = () => {
    const { selectedFacets } = this.props;
    if (!selectedFacets || Object.keys(selectedFacets).length === 0) {
      return null;
    }

    const facetItems = [];
    Object.entries(selectedFacets).forEach(([key, value]) => {
      if (value && value !== '') {
        const label = this.getFacetLabel(key);
        if (key === 'price') {
          const priceText =
            value.replace('-max', '원 이상').replace('-', '원 ~ ') +
            (value.includes('max') ? '' : '원');
          facetItems.push({ key, label, value: priceText, rawValue: value });
        } else {
          const values = value.split(',').map(v => v.trim());
          values.forEach(v => {
            facetItems.push({ key, label, value: v, rawValue: v });
          });
        }
      }
    });

    if (facetItems.length === 0) return null;

    return (
      <div className="selected-facets">
        {facetItems.map((item, index) => (
          <span key={`${item.label}-${item.value}-${index}`} className="facet-badge">
            <span className="facet-label">{item.label}</span>
            <span className="facet-value">{item.value}</span>
            <button
              className="facet-remove"
              onClick={() => this.handleRemoveFacet(item.key, item.rawValue)}
              aria-label="필터 제거"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  componentWillUnmount() {
    this.unlistenScroll();
  }

  renderProductGrid() {
    const { result, menu, currentKeyword } = this.props;
    const { badgesMap } = this.state;
    if (!result || !result.hits) return null;

    // 상품만 필터링
    const products = result.hits.filter(item => menu === 'products');

    // 배지에 color 정보 추가
    const productsWithBadgeColors = products.map(product => {
      if (product.badges && Array.isArray(product.badges)) {
        const badgesWithColor = product.badges.map(badgeName => ({
          name: badgeName,
          color: badgesMap[badgeName] || '#6c757d',
        }));
        return { ...product, badges: badgesWithColor };
      }
      return product;
    });

    return (
      <div className="product-grid">
        {productsWithBadgeColors.map((product, index) => (
          <SearchResultItem
            key={product.id}
            post={product}
            menu={menu}
            index={index}
            searchTerm={currentKeyword}
          />
        ))}
      </div>
    );
  }

  componentDidUpdate(prevProps) {
    const { currentKeyword } = this.props;
    const prevKeyword = prevProps.currentKeyword;

    // 새로운 검색어인 경우 최상단으로 스크롤 및 검색내검색 초기화
    if (currentKeyword !== prevKeyword) {
      window.scrollTo(0, 0);
      this.setState({ refinedKeyword: '' });
    }
  }

  handleRefinedKeywordChange = e => {
    this.setState({ refinedKeyword: e.target.value });
  };

  handleRefinedSearch = e => {
    e.preventDefault();
    const { refinedKeyword } = this.state;
    const { currentKeyword, onKeywordChange } = this.props;

    if (!refinedKeyword.trim()) return;

    // 기존 검색어 + 새 검색어 조합
    const combinedKeyword = currentKeyword
      ? `${currentKeyword} ${refinedKeyword}`.trim()
      : refinedKeyword;

    // props를 통한 효율적인 검색 실행
    onKeywordChange(combinedKeyword);
    this.setState({ refinedKeyword: '' });
  };

  clearRefinedKeyword = () => {
    this.setState({ refinedKeyword: '' });
  };

  removeKeyword = keywordToRemove => {
    const { currentKeyword, onKeywordChange } = this.props;
    const keywords = currentKeyword.split(' ').filter(k => k.trim());
    const newKeywords = keywords.filter(k => k !== keywordToRemove);
    const newKeyword = newKeywords.join(' ');

    // props를 통한 효율적인 검색 실행
    onKeywordChange(newKeyword);
  };

  renderKeywordTags = () => {
    const { currentKeyword } = this.props;
    if (!currentKeyword) return null;

    const keywords = currentKeyword.split(' ').filter(k => k.trim());
    // 중복 제거
    const uniqueKeywords = [...new Set(keywords)];
    if (uniqueKeywords.length <= 1) return null;

    return (
      <div className="keyword-tags">
        {uniqueKeywords.map((keyword, index) => (
          <span key={`${keyword}-${index}`} className="keyword-tag">
            <span className="keyword-text">{keyword}</span>
            <button
              type="button"
              className="keyword-remove"
              onClick={() => this.removeKeyword(keyword)}
              aria-label={`${keyword} 제거`}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  render() {
    const {
      result,
      onSearchNext,
      menu,
      currentKeyword,
      loading,
      onFacetChange,
      selectedFacets,
      onSortChange,
      currentSort,
    } = this.props;
    const q = currentKeyword.replace(/%26/g, '&').replace(/%2B/g, '+');

    // 상품 검색만 지원
    if (menu !== 'products') {
      return (
        <div className="SearchResults">
          <div className="unsupported-menu">
            <p>상품 검색만 지원됩니다.</p>
          </div>
        </div>
      );
    }

    const hasSelectedFacets =
      selectedFacets &&
      Object.keys(selectedFacets).some(key => selectedFacets[key] && selectedFacets[key] !== '');

    return (
      <div className="SearchResults">
        <div className="search-header">
          <div className="search-info">
            <h2 className="search-title">
              {currentKeyword ? (
                <span>&apos;{q}&apos; 검색 결과</span>
              ) : hasSelectedFacets ? (
                <span>필터 검색 결과</span>
              ) : (
                <span>전체 상품</span>
              )}
            </h2>
            {this.renderSelectedFacets()}
            {result && (
              <div className="result-count">
                총{' '}
                <strong>
                  {result.estimatedTotalHits ? result.estimatedTotalHits.toLocaleString() : 0}개
                </strong>
                의 상품을 찾았습니다.
              </div>
            )}
            {currentKeyword && result && result.hits && result.hits.length > 0 && (
              <form className="refined-search-form" onSubmit={this.handleRefinedSearch}>
                <div className="refined-search-input-wrapper">
                  <input
                    type="text"
                    className="refined-search-input"
                    placeholder="결과 내 재검색..."
                    value={this.state.refinedKeyword}
                    onChange={this.handleRefinedKeywordChange}
                  />
                  {this.state.refinedKeyword && (
                    <button
                      type="button"
                      className="refined-search-clear"
                      onClick={this.clearRefinedKeyword}
                      aria-label="입력 지우기"
                    >
                      ✕
                    </button>
                  )}
                  <button type="submit" className="refined-search-button">
                    <span role="img" aria-label="검색">
                      🔍
                    </span>
                    재검색
                  </button>
                </div>
                {this.renderKeywordTags()}
              </form>
            )}
          </div>
        </div>

        <div className="search-content">
          <div className="search-main">
            {loading ? (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
              </div>
            ) : null}
            {result && result.hits && result.hits.length > 0 ? (
              this.renderProductGrid()
            ) : !loading ? (
              <div className="no-results">
                {currentKeyword ? (
                  <div className="no-results-content">
                    <div className="no-results-icon">
                      <span role="img" aria-label="검색 아이콘">
                        🔍
                      </span>
                    </div>
                    <h3>검색 결과가 없습니다</h3>
                    <p>&apos;{q}&apos;와 일치하는 상품을 찾을 수 없습니다.</p>

                    <div className="no-results-tips">
                      <h4>
                        <span role="img" aria-label="아이디어">
                          💡
                        </span>{' '}
                        검색 팁
                      </h4>
                      <ul>
                        <li>철자가 정확한지 확인해주세요</li>
                        <li>더 일반적인 검색어를 사용해보세요</li>
                        <li>제품 번호나 브랜드명으로 검색해보세요</li>
                        <li>유사한 단어나 동의어를 시도해보세요</li>
                      </ul>
                    </div>

                    <div className="no-results-actions">
                      <button className="contact-button" onClick={this.onContactSupport}>
                        <span role="img" aria-label="채팅">
                          💬
                        </span>{' '}
                        제품 문의하기
                      </button>
                      <p className="contact-description">
                        원하시는 제품을 찾지 못하셨나요?
                        <br />
                        전문 상담원이 도와드리겠습니다.
                      </p>
                    </div>
                  </div>
                ) : (
                  <SearchHistory />
                )}
              </div>
            ) : null}
          </div>

          <div className="search-sidebar">
            <SearchFacets
              facetDistribution={result && result.facetDistribution}
              selectedFacets={selectedFacets}
              onFacetChange={onFacetChange}
              onSortChange={onSortChange}
              currentSort={currentSort}
              searchQuery={currentKeyword}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ pender }: State) => ({
  loading: pender.pending['search/PUBLIC_SEARCH'],
});

export default connect(mapStateToProps, () => ({}))(SearchResults);



// WEBPACK FOOTER //
// ./src/components/search/SearchResults/SearchResults.js